import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Phone, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import useDeviceDetection from '../hooks/useDeviceDetection';

const Login = ({ onLogin }) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useDeviceDetection();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Forgot Password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordIdentifier, setForgotPasswordIdentifier] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordEmailHint, setForgotPasswordEmailHint] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage(''); // Clear previous errors
    
    // Clear any stale tokens before attempting login
    localStorage.removeItem('token');
    
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      toast.success('Login successful!');
      onLogin(response.data.access_token, response.data.user);
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      
      if (status === 401) {
        setErrorMessage('Invalid credentials. Please check your email and password.');
        toast.error('Invalid credentials');
      } else if (status === 503) {
        setErrorMessage('Database temporarily unavailable. Please try again in a moment.');
        toast.error('Service temporarily unavailable');
      } else if (detail) {
        setErrorMessage(detail);
        toast.error(detail);
      } else if (error?.message?.includes('Network Error')) {
        setErrorMessage('Unable to connect to server. Please check your internet connection.');
        toast.error('Network error');
      } else {
        setErrorMessage('Something went wrong. Please try again.');
        toast.error('Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const trimmedIdentifier = forgotPasswordIdentifier.trim();
    
    if (!trimmedIdentifier) {
      toast.error('Please enter your email or mobile number');
      return;
    }
    
    setForgotPasswordLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        identifier: trimmedIdentifier
      });
      setForgotPasswordSuccess(true);
      setForgotPasswordEmailHint(response.data.email_hint || '');
      toast.success('Password reset email sent!');
    } catch (error) {
      console.error('Forgot password error:', error);
      const message = error.response?.data?.detail || 'Failed to process request. Please try again.';
      toast.error(message);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const resetForgotPasswordDialog = () => {
    setShowForgotPassword(false);
    setForgotPasswordIdentifier('');
    setForgotPasswordSuccess(false);
    setForgotPasswordEmailHint('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8 relative overflow-hidden bg-gradient-to-br from-green-50 via-white to-lime-50">
      {/* Gradient orbs */}
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-gradient-to-br from-lime-300/40 to-green-200/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-gradient-to-tl from-green-300/40 to-lime-200/20 rounded-full blur-3xl" />
      <div className="absolute top-1/4 right-1/4 w-[300px] h-[300px] bg-lime-200/30 rounded-full blur-2xl hidden md:block" />
      <div className="absolute bottom-1/4 left-1/4 w-[250px] h-[250px] bg-green-200/30 rounded-full blur-2xl hidden md:block" />
      
      {/* Normal trees pattern - round canopy style - hidden on mobile */}
      <div className="absolute inset-0 opacity-[0.12] hidden md:block" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M40 55 L40 70' stroke='%2322c55e' stroke-width='3'/%3E%3Ccircle cx='40' cy='38' r='18' fill='none' stroke='%2322c55e' stroke-width='1.5'/%3E%3C/svg%3E")`,
        backgroundSize: '80px 80px'
      }} />
      
      {/* Smaller trees offset - hidden on mobile */}
      <div className="absolute inset-0 opacity-[0.08] hidden md:block" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 42 L30 52' stroke='%2316a34a' stroke-width='2'/%3E%3Ccircle cx='30' cy='30' r='12' fill='none' stroke='%2316a34a' stroke-width='1.2'/%3E%3C/svg%3E")`,
        backgroundSize: '60px 60px',
        backgroundPosition: '30px 40px'
      }} />
      
      <div className="relative z-10 w-full max-w-lg flex flex-col items-center">
        {/* Logo - Responsive sizing */}
        <div className="text-center mb-4 md:mb-0 md:-mb-[180px]">
          <img 
            src="/customer-assets/Logo_New.png" 
            alt="Ezy Mobility" 
            className="w-auto mx-auto h-32 sm:h-48 md:h-[400px] lg:h-[500px]"
          />
        </div>

        <Card className="shadow-none border-0 bg-transparent relative w-full mt-4 md:mt-0">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl">{t('login.title')}</CardTitle>
              {/* Language Selector */}
              <div className="scale-75">
                <LanguageSelector />
              </div>
            </div>
            <CardDescription>{t('login.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              {/* Error Message */}
              {errorMessage && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 font-medium">{errorMessage}</p>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="login-email">{t('login.email')}</Label>
                <Input
                  id="login-email"
                  data-testid="login-email"
                  type="email"
                  placeholder="you@example.com"
                  value={loginData.email}
                  onChange={(e) => {
                    setLoginData({ ...loginData, email: e.target.value });
                    setErrorMessage(''); // Clear error when user types
                  }}
                  required
                  className={errorMessage ? 'border-red-300' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">{t('login.password')}</Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    data-testid="login-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => {
                      setLoginData({ ...loginData, password: e.target.value });
                      setErrorMessage(''); // Clear error when user types
                    }}
                    required
                    className={`pr-10 ${errorMessage ? 'border-red-300' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-lime-600 to-green-600 hover:from-lime-700 hover:to-green-700"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? `${t('common.loading')}` : t('login.loginButton')}
              </Button>
              
              {/* Forgot Password Link */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-lime-600 hover:text-lime-700 hover:underline font-medium"
                >
                  Forgot Password?
                </button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Forgot Password Dialog */}
        <Dialog open={showForgotPassword} onOpenChange={resetForgotPasswordDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {forgotPasswordSuccess ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Password Reset Sent
                  </>
                ) : (
                  'Forgot Password'
                )}
              </DialogTitle>
              <DialogDescription>
                {forgotPasswordSuccess 
                  ? 'Check your email for the new password.'
                  : 'Enter your registered email or mobile number to reset your password.'}
              </DialogDescription>
            </DialogHeader>
            
            {forgotPasswordSuccess ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-green-800 font-medium mb-2">Password Reset Email Sent!</p>
                  <p className="text-green-700 text-sm">
                    We've sent a new temporary password to:
                  </p>
                  <p className="text-green-800 font-mono text-lg mt-1">
                    {forgotPasswordEmailHint}
                  </p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-800 text-sm">
                    <strong>Note:</strong> Please check your spam folder if you don't see the email. After logging in, we recommend changing your password from the Profile page.
                  </p>
                </div>
                <Button 
                  onClick={resetForgotPasswordDialog}
                  className="w-full bg-gradient-to-r from-lime-600 to-green-600"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-identifier">Email or Mobile Number</Label>
                  <div className="relative">
                    <Input
                      id="forgot-identifier"
                      type="text"
                      placeholder="you@example.com or +971 50 123 4567"
                      value={forgotPasswordIdentifier}
                      onChange={(e) => setForgotPasswordIdentifier(e.target.value)}
                      className="pl-10"
                      required
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {forgotPasswordIdentifier.includes('@') ? (
                        <Mail className="w-4 h-4" />
                      ) : (
                        <Phone className="w-4 h-4" />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Enter the email or mobile number associated with your account.
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForgotPasswordDialog}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={forgotPasswordLoading}
                    className="flex-1 bg-gradient-to-r from-lime-600 to-green-600"
                  >
                    {forgotPasswordLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <div className="text-center text-sm text-gray-500">
          © 2025 Ezy Mobility. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default Login;
